import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/api/client";
import { chime, mentionChime, reminder, unlockAudio, weeklyReportChime } from "@/utils/sound";
import { notify, requestPermission, permission as permStatus } from "@/utils/desktopNotify";
import { registerPush } from "@/utils/pushClient";
import { useAuth } from "@/context/AuthContext";
import { fmtTime, todayIstIso } from "@/utils/date";

const REMINDER_HOURS = [10, 14, 17]; // local 10:00, 14:00, 17:00
const POLL_MS = 20000;

/**
 * Poll notifications + fire in-app toasts + desktop notifications + sound.
 * Also schedules three daily "work pending" reminders.
 * Exposes a small "Enable desktop alerts" prompt if permission is 'default'.
 */
export default function NotificationCenter({ onNotifs }) {
  const { user } = useAuth();
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const [needsPrompt, setNeedsPrompt] = useState(false);

  // Unlock audio + read desktop-notification permission on first user gesture
  useEffect(() => {
    const unlock = async () => {
      unlockAudio();
      if ("Notification" in window && Notification.permission === "default") {
        setNeedsPrompt(true);
      }
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const enableDesktop = async () => {
    const result = await requestPermission();
    setNeedsPrompt(false);
    if (result === "granted") {
      notify("LIM Flow", { body: "Desktop alerts enabled. You'll get notified for new tasks and reminders.", tag: "lim-welcome" });
      toast.success("Desktop alerts enabled");
      // Register service worker + subscribe for push notifications (works when tab is closed)
      registerPush().then((ok) => {
        if (ok) toast.success("Push notifications active — you'll get pings even when the tab is closed.");
      });
    } else {
      toast.info("Desktop alerts blocked — you can enable them later in browser settings.");
    }
  };

  // Auto-register push if permission was already granted in an earlier visit
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("Notification" in window && Notification.permission === "granted") {
      registerPush();
    }
  }, []);
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!alive) return;
        onNotifs?.(data);
        const seen = seenIdsRef.current;
        const fresh = data.filter((n) => !seen.has(n.id));
        if (!firstLoadRef.current) {
          const unreadFresh = fresh.filter((n) => !n.read);
          if (unreadFresh.length > 0) {
            const hasMention = unreadFresh.some((n) => n.kind === "mention");
            const hasWeekly = unreadFresh.some((n) => n.kind === "weekly_report_reminder" || n.kind === "weekly_report_upload");
            if (hasWeekly) weeklyReportChime();
            else if (hasMention) mentionChime();
            else chime();
            unreadFresh.slice(0, 3).forEach((n) => {
              const isMention = n.kind === "mention";
              const isWeekly = n.kind === "weekly_report_reminder" || n.kind === "weekly_report_upload";
              toast(n.text, {
                description: fmtTime(n.created_at),
                className: isMention
                  ? "border-[#E11D2E]/50"
                  : isWeekly
                  ? "border-amber-500/50"
                  : undefined,
              });
              notify(
                isMention
                  ? "LIM Flow — You were mentioned"
                  : isWeekly
                  ? "LIM Flow — Weekly report"
                  : "LIM Flow — Task update",
                {
                  body: n.text,
                  tag: `lim-notif-${n.id}`,
                  url: "/",
                  requireInteraction: isMention || isWeekly,
                },
              );
            });
          }
        }
        firstLoadRef.current = false;
        data.forEach((n) => seen.add(n.id));
      } catch (e) { /* silent */ }
    };
    load();
    const int = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(int); };
  }, [user, onNotifs]);

  // Daily-log reminders at 10 / 14 / 17
  useEffect(() => {
    if (!user) return undefined;
    const key = (d, h) => `lim_reminded_${d}_${h}`;
    const check = async () => {
      const now = new Date();
      // Use IST for hour + date so India-based reminders fire correctly
      // regardless of the browser's timezone.
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata", hour12: false, hour: "2-digit", minute: "2-digit",
      }).formatToParts(now);
      const h = Number(parts.find((p) => p.type === "hour")?.value);
      const m = Number(parts.find((p) => p.type === "minute")?.value);
      if (!REMINDER_HOURS.includes(h)) return;
      if (m > 5) return;
      const dateStr = todayIstIso(now);
      const flag = key(dateStr, h);
      if (localStorage.getItem(flag)) return;

      try {
        const [tRes, lRes] = await Promise.all([
          api.get("/tasks"),
          api.get("/daily-logs", { params: { date_from: dateStr, date_to: dateStr } }),
        ]);
        const myTasks = tRes.data.filter((t) => t.assigned_to === user.id);
        const pending = myTasks.filter((t) => t.status !== "complete");
        const loggedToday = lRes.data.length > 0;

        if (pending.length > 0 || !loggedToday) {
          reminder();
          const body = loggedToday
            ? `You still have ${pending.length} open task${pending.length === 1 ? "" : "s"}.`
            : `Don't forget to file today's daily log${pending.length ? ` — ${pending.length} pending` : ""}.`;
          const slotLabel = h === 10 ? "Morning check-in" : h === 14 ? "Afternoon check-in" : "End-of-day check-in";
          toast.warning(`Work pending today — ${slotLabel}`, {
            description: body,
            duration: 12000,
            action: {
              label: "Open daily log",
              onClick: () => { window.location.href = "/daily-log"; },
            },
          });
          notify(`LIM Flow — ${slotLabel}`, {
            body,
            tag: `lim-reminder-${dateStr}-${h}`,
            url: "/daily-log",
            requireInteraction: h === 17,
          });
        }
        localStorage.setItem(flag, "1");
      } catch (e) { /* silent */ }
    };

    check();
    const int = setInterval(check, 60_000);
    return () => clearInterval(int);
  }, [user]);

  // Small floating prompt to enable desktop alerts (only when permission is 'default')
  if (!needsPrompt) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-xs bg-surface border border-white/10 rounded-lg p-4 shadow-2xl animate-fade-in-up">
      <div className="flex items-start gap-3">
        <div className="h-10 w-14 rounded overflow-hidden shrink-0">
          <img src="/lim-logo-small.png" alt="LIM" className="h-full w-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="font-display text-sm mb-1">Enable desktop alerts?</div>
          <div className="text-xs text-white/60 leading-relaxed">
            Get floating notifications on your desktop for new tasks, changes, and daily reminders.
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={enableDesktop}
              data-testid="desktop-notif-enable"
              className="text-xs px-3 py-1.5 rounded-full bg-[#E11D2E] hover:bg-[#F03041] text-white"
            >
              Enable
            </button>
            <button
              onClick={() => setNeedsPrompt(false)}
              className="text-xs px-3 py-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/5"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
